<script lang="ts">
    import { _ } from "svelte-i18n"
    let {
        quantity = $bindable(1),
        callback,
        product,
    }: {
        quantity?: number
        callback?: (quantity: number) => void
        product?: Product
    } = $props()

    // Check if maximum quantity is reached
    let maxReached = $derived(
        product?.maximum_order_quantity && product.maximum_order_quantity > 0
            ? quantity >= product.maximum_order_quantity
            : false
    )

    let inputValue = $state(String(quantity))

    // Sync inputValue when quantity changes externally (e.g. via +/- buttons)
    $effect(() => {
        inputValue = String(quantity)
    })

    function applyInputValue() {
        const parsed = parseInt(inputValue, 10)
        if (isNaN(parsed) || parsed < 1) {
            quantity = 1
        } else if (
            product?.maximum_order_quantity &&
            product.maximum_order_quantity > 0 &&
            parsed > product.maximum_order_quantity
        ) {
            quantity = product.maximum_order_quantity
        } else {
            quantity = parsed
        }
        inputValue = String(quantity)
        callback && callback(quantity)
    }
</script>

<div class="counter">
    <span class="label">{$_("product.quantity")}</span>
    <div class="controls">
        <button
            class="button transparent"
            disabled={quantity <= 1}
            title={quantity <= 1 ? $_("product.tooltip-min-quantity") : ""}
            onclick={() => {
                if (quantity <= 1) return
                quantity--
                callback && callback(quantity)
            }}
            aria-label="Decrease quantity"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M5 12.5V11.5H19V12.5H5Z" fill="#464646"></path>
            </svg>
        </button>
        <input
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            bind:value={inputValue}
            onblur={applyInputValue}
            onkeydown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault()
                    applyInputValue()
                }
            }}
            aria-label={$_("product.quantity")}
        />
        <button
            class="button transparent"
            disabled={maxReached}
            title={maxReached ? $_("product.tooltip-max-quantity") : ""}
            onclick={() => {
                if (maxReached) return
                quantity++
                callback && callback(quantity)
            }}
            aria-label="Increase quantity"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M11.5 12.5H6V11.5H11.5V6H12.5V11.5H18V12.5H12.5V18H11.5V12.5Z" fill="#464646"></path>
            </svg>
        </button>
    </div>
</div>

<style lang="less">
    @import "../../../../css/variables.less";
    .counter {
        display: flex;
        flex-direction: column;
        align-items: center;
        .label {
            color: var(--grey-300);
        }
        .controls {
            display: flex;
            gap: 0.3rem;
            align-items: center;
            @media @max-mobile {
                gap: 0.15rem;
            }
            button {
                padding: 0;
                margin: 0;
                line-height: 0;
                height: auto;
            }
            input {
                color: var(--grey-400);
                font-size: 1rem;
                line-height: 140%;
                text-align: center;
                width: 3rem;
                border: 1px solid var(--grey-200);
                border-radius: 4px;
                padding: 2px 4px;
                margin: 0;
                background: transparent;
                appearance: textfield;
                -moz-appearance: textfield;
                &::-webkit-outer-spin-button,
                &::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                &:focus {
                    outline: 2px solid var(--primary);
                    outline-offset: -1px;
                }
            }
        }
    }
</style>
